import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useSite } from "@/lib/site";

const WA_FADE_IN_MS = 800;

// Inline WhatsApp glyph (lucide-react has no WhatsApp icon, use official SVG path)
function WhatsAppGlyph({ className = "w-7 h-7" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export default function WhatsAppButton() {
  const { site } = useSite();
  const w = site.whatsapp || { number: "919808724448", message: "Hi, I'm interested in your services." };
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Fade in on mount — no dependencies needed, one-shot timer
    const t = setTimeout(() => setVisible(true), WA_FADE_IN_MS);
    return () => clearTimeout(t);
  }, []);

  const href = `https://wa.me/${w.number}?text=${encodeURIComponent(w.message).replace(/'/g, "%27")}`;

  return (
    <div className={`fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`} data-testid="whatsapp-widget">
      {open && (
        <div className="bg-white rounded-2xl shadow-2xl border border-[color:var(--dh-line)] p-4 w-72 md:w-80 animate-[reveal-up_.25s_ease]" data-testid="whatsapp-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <img src="/logo-256.png" alt="Digi Hub Solution" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="font-display font-bold text-[color:var(--dh-navy)] leading-tight">Digi Hub Solution</div>
                <div className="text-xs text-green-600 font-semibold">● Online</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="whatsapp-close" className="p-1 rounded-full hover:bg-gray-100">
              <X className="w-4 h-4 text-[color:var(--dh-slate)]" />
            </button>
          </div>
          <p className="mt-3 text-sm text-[color:var(--dh-slate)] leading-relaxed">
            Hi there! 👋 <br />How can we help you today? Chat with us on WhatsApp and we'll respond quickly.
          </p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-start-chat"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold py-2.5 rounded-full transition-colors"
          >
            <WhatsAppGlyph className="w-4 h-4" /> Start Chat
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="WhatsApp us"
        data-testid="whatsapp-toggle"
        className="w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white grid place-items-center shadow-2xl hover:scale-105 active:scale-95 transition-transform relative"
      >
        <WhatsAppGlyph className="w-7 h-7" />
        {!open && (
          <span className="absolute inset-0 rounded-full animate-ping bg-[#25D366]/40 pointer-events-none" />
        )}
      </button>
    </div>
  );
}
